import {
  type NoteEditorRenderedVariant,
  type NoteEditorType,
} from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import { type EditorTelemetrySurface } from '@/utils/telemetry/editor-observability';
import { datadogRum } from '@datadog/browser-rum';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import type { Editor } from '@tiptap/react';

/** RUM action name — pairs with the `note_editor_rendered` denominator. */
export const NOTE_EDITOR_RENDER_FAILED_ACTION = 'note_editor_render_failed';

/** Stable code for the markdown-native hydrate/serialize failure path. */
export const MARKDOWN_NATIVE_RENDER_FAILED_CODE = 'MARKDOWN_NATIVE_SET_CONTENT_FAILED';

/** Post-stream canonical re-apply failed — the doc still holds preview HTML. */
export const MARKDOWN_NATIVE_FINALIZE_FAILED_CODE = 'MARKDOWN_NATIVE_FINALIZE_FAILED';

/** Stable code for a failed `setContent` on the shared HTML apply path. */
export const SET_CONTENT_FAILED_CODE = 'SET_CONTENT_FAILED';

/** Content rendered, caret restore threw — exclude from a render-success SLI. */
export const SELECTION_RESTORE_FAILED_CODE = 'SELECTION_RESTORE_FAILED';

/**
 * Nothing threw — the failure is the absence of a live instance inside the
 * caller's window. Read it as "not ready in time", never "never": a stall
 * that later recovers still reports, alongside its `note_editor_rendered`.
 */
export const EDITOR_READY_TIMEOUT_CODE = 'EDITOR_READY_TIMEOUT';

/**
 * Closed set of `error_code` values for a failure that *had* content to render
 * — keeps the dashboard dimension typo-proof. Deliberately excludes
 * `EDITOR_READY_TIMEOUT`, so the timeout code cannot be handed to an entry
 * point that would attach content dimensions it never had.
 */
export type NoteEditorRenderFailedCode =
  | typeof MARKDOWN_NATIVE_RENDER_FAILED_CODE
  | typeof MARKDOWN_NATIVE_FINALIZE_FAILED_CODE
  | typeof SET_CONTENT_FAILED_CODE
  | typeof SELECTION_RESTORE_FAILED_CODE;

/** Every code the RUM action can carry, content-bearing or not. */
type RenderFailedActionCode =
  | NoteEditorRenderFailedCode
  | typeof EDITOR_READY_TIMEOUT_CODE;

/** Hydration format attempted: 'markdown' for canonical notes, 'html' for legacy. */
export type NoteEditorRenderContentType = 'markdown' | 'html';

/**
 * Note-tab membership for the render-success SLI. Absent everywhere else: RUM
 * separates other surfaces by `@view.url` on its own.
 */
export type NoteEditorSurface = 'note' | 'note_offscreen';

/**
 * Narrows a lifecycle telemetry surface to note-tab render-SLI membership.
 * Whitelisted rather than excluding the known non-note surfaces: a new
 * lifecycle-only surface must not join the SLI just because nobody remembered
 * to exclude it at each editor hook. All three editor hooks route their
 * content-sync `surface` through here, so the membership rule cannot drift
 * between kits.
 */
export function toNoteEditorSurface(
  surface: EditorTelemetrySurface | undefined
): NoteEditorSurface | undefined {
  return surface === 'note' || surface === 'note_offscreen' ? surface : undefined;
}

/**
 * Builds the RUM payload. Carries no note body and no raw error string — the
 * raw error stays in the adjacent `logger.error`, where a ProseMirror message
 * echoing note content cannot reach telemetry. Anything added here must hold
 * that line.
 *
 * The content dimensions are optional here and required on every public entry
 * point that has them, so only the mount-timeout path can omit them.
 */
const emitRenderFailedAction = ({
  sessionId,
  editorType,
  contentType,
  contentLength,
  errorCode,
  surface,
}: {
  sessionId?: string;
  // Only the ready-timeout path can carry 'readonly_whole_note' (and never
  // does, by construction); the other entry points keep the editor-only
  // vocabulary.
  editorType: NoteEditorRenderedVariant;
  contentType?: NoteEditorRenderContentType;
  contentLength?: number;
  errorCode: RenderFailedActionCode;
  surface?: NoteEditorSurface;
}): void => {
  try {
    datadogRum.addAction(NOTE_EDITOR_RENDER_FAILED_ACTION, {
      // Not the URL auto-attach — its regex misses `/scribe/sessions/<id>`.
      // A caller that already holds the id passes it: the router is wrong for
      // an embedded pane (no `/scribe/session` path) and for any deferred
      // emit, which would otherwise stamp whatever session is current later.
      // `||`, not `??`: an empty id is as wrong as a missing one.
      session_id: sessionId || getCurrentSessionId(),
      editor_type: editorType,
      ...(contentType === undefined ? {} : { content_type: contentType }),
      ...(contentLength === undefined ? {} : { content_length: contentLength }),
      error_code: errorCode,
      // Omitted, never defaulted: absent must not be mistaken for `note`.
      ...(surface ? { surface } : {}),
    });
  } catch (emitError) {
    logger.warn('[note-editor] render-failed telemetry emit failed', emitError);
  }
};

/**
 * The editor was expected but never became ready, so there is no editor to key
 * a WeakMap on and no content attempt to describe — hence its own entry point
 * rather than the `-Once` variant. Callers dedupe by session and editor type.
 *
 * `sessionId` is required, not derived: it has to match the id
 * `note_editor_rendered` carries or the two cannot be paired.
 */
export const emitNoteEditorReadyTimeout = ({
  sessionId,
  editorType,
  surface,
}: {
  sessionId: string;
  // The `note_editor_rendered` variant vocabulary: the read-only whole-note
  // view reports through the same usage hook, but its expected and ready
  // inputs are the same value by construction, so the timeout can never fire
  // for it; the union is admitted so the shared hook stays type-honest.
  editorType: NoteEditorRenderedVariant;
  surface?: NoteEditorSurface;
}): void =>
  emitRenderFailedAction({
    sessionId,
    editorType,
    errorCode: EDITOR_READY_TIMEOUT_CODE,
    surface,
  });

/**
 * RUM, not the product-analytics pipeline: this is a reliability signal feeding
 * monitors and the render-success SLO, and only RUM can alert on it.
 *
 * Carries no note body and no raw error string — the raw error stays in the
 * adjacent `logger.error`, where a ProseMirror message echoing note content
 * cannot reach telemetry.
 */
export const emitNoteEditorRenderFailed = ({
  editorType,
  contentType,
  contentLength,
  errorCode,
  surface,
}: {
  editorType: NoteEditorType;
  contentType: NoteEditorRenderContentType;
  contentLength: number;
  errorCode: NoteEditorRenderFailedCode;
  surface?: NoteEditorSurface;
}): void => {
  emitRenderFailedAction({ editorType, contentType, contentLength, errorCode, surface });
};

/**
 * One event per editor per code per surface. The apply runs on every content
 * change, and once a frame while streaming, so an unparseable note would emit
 * unboundedly. `note_editor_rendered` dedupes per session and editor type, so
 * every arm has to count editors or the arms cannot be compared. Logs stay
 * unthrottled. Surface is in the key because one editor instance crosses
 * `note_offscreen`→`note` on an in-session engine switch, and keying on the
 * code alone would swallow the numerator for the re-fired denominator.
 */
const emittedKeysByEditor = new WeakMap<Editor, Set<string>>();

const dedupeKey = (
  errorCode: NoteEditorRenderFailedCode,
  surface: NoteEditorSurface | undefined
): string => `${errorCode}:${surface ?? 'none'}`;

export const emitNoteEditorRenderFailedOnce = (
  editor: Editor,
  params: Parameters<typeof emitNoteEditorRenderFailed>[0]
): void => {
  const key = dedupeKey(params.errorCode, params.surface);
  const emitted = emittedKeysByEditor.get(editor) ?? new Set<string>();
  if (emitted.has(key)) return;
  emitted.add(key);
  emittedKeysByEditor.set(editor, emitted);
  emitNoteEditorRenderFailed(params);
};
